'use client';

/** @jsxImportSource @emotion/react */
import {
  Box,
  Chip,
  Container,
  FlexItem,
  FlexLayout,
  Icon,
  Stepper,
  Typography,
  useTheme,
} from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { RiCheckboxBlankCircleFill } from 'react-icons/ri';
import { useStyles } from './HowItWorks.styles';
import { getCommonChipStyles } from '@/styles/Common.styles';
import { useTranslations } from 'next-intl';
import { useThemeMode } from '@/context';

const HowItWorks = () => {
  const styles = useStyles();
  const { theme } = useTheme();
  const t = useTranslations('HowItWorks');
  const commonChip = getCommonChipStyles(theme);
  const { isDark } = useThemeMode();
  // Define keys to map through from the translation JSON
  const stepKeys = ['step1', 'step2', 'step3', 'step4', 'step5', 'step6'] as const;

  const customSvgContentItems = stepKeys.map((key, index) => ({
    content: (
      <Box
        borderRadius="20px"
        customStyles={styles.box}
        children={
          <FlexLayout
            direction="column"
            alignItems="center"
            gap={{ default: '18px', md: '24px', lg: '36px' }}
            padding={{ default: 0 }}
          >
            <FlexItem>
              <Typography variant="p" css={styles.steplabel} textTransform="uppercase">
                {/* Translating "Step X" + the dynamic label */}
                STEP {index + 1} - {t(`steps.${key}.label`)}
              </Typography>
              <Typography variant="p" css={styles.stepTitle}>
                {t(`steps.${key}.title`)}
              </Typography>
              <Typography variant="p" css={styles.stepDescription}>
                {t(`steps.${key}.description`)}
              </Typography>
            </FlexItem>

            <FlexItem alignSelf="flex-start">
              <Typography variant="p" css={styles.secondarySteplabel}>
                {t(`steps.${key}.footerLabel`)}
              </Typography>
              <Typography variant="p" css={styles.stepDescription}>
                {t(`steps.${key}.footerDesc`)}
              </Typography>
            </FlexItem>
          </FlexLayout>
        }
      />
    ),
    icon: <Icon name={RiCheckboxBlankCircleFill} size={34} color={isDark ? '#000' : '#fff'} />,
  }));

  return (
    <section css={styles.wrapper} id="how-it-works">
      <Container css={styles.container}>
        <FlexLayout
          direction="column"
          alignItems="center"
          gap={{ default: 0 }}
          padding={{ default: 0 }}
        >
          <FlexItem>
            <Chip
              label={t('chipLabel')}
              enableHoverEffect={false}
              customStyles={{
                chipContainer: commonChip.chipContainer,
                label: commonChip.chipLabel,
              }}
            />
          </FlexItem>

          <FlexItem css={styles.headerSection}>
            <FlexLayout direction="column" alignItems="center" gap={16} padding={{ default: 0 }}>
              <Typography variant="h2" customStyles={styles.title}>
                {t('title')}
              </Typography>
              <Typography variant="p" customStyles={styles.description}>
                {t('description')}
              </Typography>
            </FlexLayout>
          </FlexItem>

          <FlexItem css={{ width: '100%', maxWidth: '1100px' }}>
            <Stepper
              effect="slideIn"
              appearance="border"
              items={customSvgContentItems}
              indicatorType="icon"
              orientation="vertical"
              variant="linear-gradient"
              customStyles={{
                stepConnector: {
                  progressLineFill: {
                    '--stepper-connector-fill-color': `linear-gradient(
      235deg,
      #4054d1 -1.18%,
      #17ceca 112.11%
    )`,
                  },
                },
                stepIndicator: {
                  indicatorFill: css`
                    background: linear-gradient(180deg, #17ceca 0%, #4054d1 100%);
                    border: transparent;
                  `,
                },
              }}
            />
          </FlexItem>
        </FlexLayout>
      </Container>
    </section>
  );
};

export default HowItWorks;
